import { db, type LocalPrayer, type LocalPrayerListItem } from '../model/db.js';

/**
 * Local (Dexie) equivalents of papi's prayer search/paging queries - see
 * `apps/papi/src/helpers/search.ts` and the `GET_ALL_*` / `GET_ITEMS_FOR_LIST*`
 * SQL for the behaviour these mirror. Rows awaiting a pushed delete
 * (`pendingDelete`) are treated as already gone everywhere.
 */

export const PRAYER_PAGE_SIZE = 25;
export const LIST_ITEM_PAGE_SIZE = 25;

/** What a paged prayer/list item list holds - cards look the rest up by id. */
export interface PrayerPageItem {
  id: string;
  updateDate: string;
}

export interface PrayerPageFilters {
  /** `date-asc` or `date-desc` (the `prayerSort` setting) - sorts on `updateDate`. */
  sortOrder: string;
  showCompleted: boolean;
  /** Case-insensitive substring match on the prayer's text, or any of its list items' text. */
  searchText?: string;
  /** Tag *text* to filter on. */
  tags?: string[];
  /** All of `tags` must be present (vs. any of them). */
  matchAllTags?: boolean;
  limit?: number;
}

export interface ListItemPageFilters {
  sortOrder: string;
  showCompleted: boolean;
  searchText?: string;
  limit?: number;
}

const isVisible = (row: { syncStatus?: string }): boolean => row.syncStatus !== 'pendingDelete';

const normalizedSearch = (searchText: string | undefined): string | undefined => {
  const trimmed = searchText?.trim().toLocaleLowerCase();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Builds the tag predicate for `fetchPrayerPage`, resolving filter tag text
 * against the local catalog at query time (rather than from a separately
 * live-queried copy, which can still be empty on first render). Returns
 * `undefined` for "no tag filter".
 */
const buildTagPredicate = async (
  tags: string[] | undefined,
  matchAllTags: boolean
): Promise<((prayer: LocalPrayer) => boolean) | undefined> => {
  if (!tags || tags.length < 1) return undefined;

  // tag text -> every local id carrying it (a not-yet-reconciled
  // placeholder can briefly share text with its canonical row)
  const catalog = await db.prayerHashtags.toArray();
  const idsByTag = new Map<string, string[]>();
  for (const row of catalog) {
    idsByTag.set(row.tag, [...(idsByTag.get(row.tag) ?? []), row.id]);
  }
  const idGroups = tags.map((tag) => idsByTag.get(tag) ?? []);

  return (prayer) => {
    const prayerTagIds = prayer.hashtagIds ?? [];
    if (prayerTagIds.length < 1) return false;

    const hasTag = (ids: string[]) => ids.some((id) => prayerTagIds.includes(id));
    return matchAllTags ? idGroups.every(hasTag) : idGroups.some(hasTag);
  };
};

/**
 * One page of prayers, sorted on `updateDate`, starting after `cursor` (the
 * `updateDate` of the last item already loaded). Unlike the server's "all
 * tags" search - which filtered each fetched page after the fact, so pages
 * could come back short or even empty mid-list - every filter here is
 * applied before the page is cut.
 */
export const fetchPrayerPage = async (
  cursor: string | undefined,
  filters: PrayerPageFilters
): Promise<PrayerPageItem[]> => {
  const limit = filters.limit ?? PRAYER_PAGE_SIZE;
  const descending = filters.sortOrder === 'date-desc';
  const search = normalizedSearch(filters.searchText);
  const tagPredicate = await buildTagPredicate(filters.tags, filters.matchAllTags ?? false);

  // lists whose (visible, and if hidden-completed, uncompleted) items match
  // the search text - matching an item counts as matching its list
  let listIdsMatchingSearch = new Set<string>();
  if (search) {
    const matchingItems = await db.prayerListItems
      .filter(
        (item) =>
          isVisible(item) &&
          (filters.showCompleted || !item.completed) &&
          item.itemText.toLocaleLowerCase().includes(search)
      )
      .toArray();
    listIdsMatchingSearch = new Set(matchingItems.map((item) => item.prayerListId));
  }

  // the cursor narrows via the index itself, so later pages don't re-scan
  // everything before them
  let query =
    cursor === undefined
      ? db.prayers.orderBy('updateDate')
      : descending
        ? db.prayers.where('updateDate').below(cursor)
        : db.prayers.where('updateDate').above(cursor);
  if (descending) query = query.reverse();

  const rows = await query
    .and((prayer) => {
      if (!isVisible(prayer)) return false;
      if (!filters.showCompleted && prayer.completed) return false;
      if (search && !prayer.text.toLocaleLowerCase().includes(search) && !listIdsMatchingSearch.has(prayer.id)) {
        return false;
      }
      if (tagPredicate && !tagPredicate(prayer)) return false;

      return true;
    })
    .limit(limit)
    .toArray();

  return rows.map((prayer) => ({ id: prayer.id, updateDate: prayer.updateDate }));
};

/**
 * One page of a list's items, sorted on `updateDate` and starting after
 * `cursor`, like {@link fetchPrayerPage}. Lists are small, so this sorts in
 * memory rather than needing a compound index.
 */
export const fetchListItemPage = async (
  listId: string,
  cursor: string | undefined,
  filters: ListItemPageFilters
): Promise<PrayerPageItem[]> => {
  const limit = filters.limit ?? LIST_ITEM_PAGE_SIZE;
  const descending = filters.sortOrder === 'date-desc';
  const search = normalizedSearch(filters.searchText);

  const items = await db.prayerListItems
    .where('prayerListId')
    .equals(listId)
    .filter((item: LocalPrayerListItem) => {
      if (!isVisible(item)) return false;
      if (!filters.showCompleted && item.completed) return false;
      if (search && !item.itemText.toLocaleLowerCase().includes(search)) return false;
      if (cursor !== undefined && (descending ? item.updateDate >= cursor : item.updateDate <= cursor)) {
        return false;
      }

      return true;
    })
    .toArray();

  items.sort((a, b) =>
    descending ? b.updateDate.localeCompare(a.updateDate) : a.updateDate.localeCompare(b.updateDate)
  );

  return items.slice(0, limit).map((item) => ({ id: item.id, updateDate: item.updateDate }));
};

/**
 * Distinct tag text from the local prayer hashtag catalog, sorted, optionally
 * narrowed to a case-insensitive substring match - replaces `/pray/ht?q=`.
 * The catalog only holds tags still used by a (non-deleted) prayer, since
 * the server tombstones orphaned ones.
 */
export const searchPrayerHashtags = async (searchText?: string): Promise<string[]> => {
  const search = normalizedSearch(searchText);
  const rows = await db.prayerHashtags.toArray();

  const tags = rows.map((row) => row.tag).filter((tag) => !search || tag.toLocaleLowerCase().includes(search));
  return Array.from(new Set(tags)).sort((a, b) => a.localeCompare(b));
};

export interface PrayerListSummary {
  id: string;
  text: string;
}

/**
 * Every local (non-deleted) prayer list, most recently updated first - the
 * targets for "attach to list" / "move to list".
 */
export const getAllPrayerLists = async (): Promise<PrayerListSummary[]> => {
  const lists = await db.prayers
    .orderBy('updateDate')
    .reverse()
    .filter((prayer) => prayer.isList && isVisible(prayer))
    .toArray();

  return lists.map((list) => ({ id: list.id, text: list.text }));
};
